// Smart Enquiry Form V1 — branch configuration.
// Structured so the form renderer stays generic and a future CMS/dashboard
// can consume the same keys. Values are stored as human-readable labels.

export const PILLAR_CANONICAL = {
  Animals: "animals",
  "People / Mental Wellbeing": "people",
  Nature: "nature",
};

export const CONTACT_METHODS = ["Email", "WhatsApp", "Phone"];

export const STATUS_NOTICE =
  "The PilCoo Foundation is currently an independent, community-led initiative and is not a registered NGO. We act as a connector, facilitator and catalyst — helping bring people, resources and credible organisations together around verified needs and meaningful action.";

export const NEED_NOTICE =
  "PilCoo is not an emergency response or rescue service. Submitting a need does not guarantee rescue, veterinary treatment, financial support or other assistance. Where possible, PilCoo may help connect the situation with appropriate professionals, volunteers or organisations.";

export const WELLBEING_NOTICE =
  "PilCoo's mental-wellbeing work is awareness and community support only — we do not provide medical advice, diagnosis, psychotherapy or crisis intervention. If someone needs urgent help, please contact a qualified professional or local emergency service.";

export const CONTACT_CONSENT_LABEL =
  "I'm happy for The PilCoo Foundation to contact me regarding this submission.";

export const SUPPORT_CIRCLE_CONSENT_LABEL =
  "I'm happy for The PilCoo Foundation to contact me in the future when a relevant opportunity to help arises. I understand that joining the Support Circle does not create any obligation to contribute.";

const PILLAR_OPTIONS_ANY = ["Animals", "People / Mental Wellbeing", "Nature", "Any", "Not sure"];
const PILLAR_OPTIONS_MULTI = ["Animals", "People / Mental Wellbeing", "Nature", "More than one", "Not sure yet"];

export const ENQUIRY_CATEGORIES = [
  { value: "volunteer", label: "I want to volunteer" },
  { value: "support-circle", label: "I want to join the Support Circle" },
  { value: "offer-help", label: "I want to offer help or resources" },
  { value: "organisation", label: "I represent an organisation" },
  { value: "cause", label: "I know a cause that needs support" },
  { value: "collaborate", label: "I want to collaborate" },
  { value: "general", label: "General enquiry" },
];

// Field types: chips (multi-select), choice (single-select), text, textarea, date
// other: true appends an "Other" chip/option that reveals a `{key}_other` text field
// showIf: { key, oneOf: [...] } — field visible only when that field's value matches
// pillarField: true — selected pillar labels feed the top-level `pillars` array
export const BRANCHES = {
  volunteer: {
    note: "Choose this if you'd like to offer your time, skills or expertise to support PilCoo activities and initiatives. Tell us what you're comfortable helping with and when you may be available.",
    fields: [
      {
        key: "help_areas",
        label: "What would you be comfortable helping with?",
        type: "chips",
        required: true,
        other: true,
        options: [
          "On-ground activities", "Event coordination", "Animal welfare activities",
          "Veterinary support", "Mental wellbeing / awareness initiatives",
          "Sustainability / environmental activities", "Social media & content",
          "Writing / copywriting", "Design / photography", "Technology",
          "Legal / compliance", "Finance / accounting", "Logistics / transport",
          "Community outreach",
        ],
      },
      {
        key: "pillars",
        label: "Which PilCoo pillar(s) interest you?",
        type: "chips",
        required: true,
        pillarField: true,
        options: ["Animals", "People / Mental Wellbeing", "Nature", "Wherever help is needed"],
      },
      {
        key: "availability_mode",
        label: "How would you prefer to volunteer?",
        type: "chips",
        required: true,
        options: ["In person", "Remote", "Weekdays", "Weekends", "Flexible"],
      },
      {
        key: "time_commitment",
        label: "How much time might you typically be able to offer?",
        type: "choice",
        required: true,
        options: ["A few hours occasionally", "A few hours each month", "1–2 days each month", "More regularly", "Depends on the opportunity"],
      },
      { key: "skills_experience", label: "Tell us about any relevant skills or experience", type: "textarea", required: false },
      { key: "why_pilcoo", label: "Why PilCoo?", type: "textarea", required: false },
    ],
  },

  "support-circle": {
    note: "Choose this if you'd like PilCoo to reach out when a verified need matches the causes or kinds of support you care about. Joining the Support Circle does not commit you to helping every time.",
    fields: [
      {
        key: "support_interests",
        label: "What would you like to support?",
        type: "chips",
        required: true,
        pillarField: true,
        options: ["Animals", "People / Mental Wellbeing", "Nature", "Any verified PilCoo need"],
      },
      {
        key: "support_types",
        label: "How might you be comfortable helping?",
        type: "chips",
        required: true,
        other: true,
        options: ["Financial support", "Time / volunteering", "Professional skills", "Supplies / materials", "Food", "Transport / logistics", "Venue / space", "Awareness / amplification"],
      },
      {
        key: "notification_frequency",
        label: "What kinds of notifications would you like?",
        type: "choice",
        required: true,
        options: ["Urgent matching needs", "All matching opportunities", "Occasional / periodic updates"],
      },
      {
        key: "notification_channels",
        label: "Preferred notification channel",
        type: "chips",
        required: true,
        options: ["Email", "WhatsApp"],
      },
    ],
    extraConsent: true,
  },

  "offer-help": {
    note: "Choose this if you have something specific you may be able to contribute — such as supplies, transport, professional expertise, space, technology, services or financial support.",
    fields: [
      {
        key: "offerings",
        label: "What would you like to offer?",
        type: "chips",
        required: true,
        other: true,
        options: ["Professional expertise", "Volunteers / people", "Products or supplies", "Food", "Transport / logistics", "Venue / space", "Technology", "Professional services", "Financial support", "Media / content reach", "Educational resources"],
      },
      {
        key: "pillars",
        label: "Which pillar could this support?",
        type: "chips",
        required: true,
        pillarField: true,
        options: PILLAR_OPTIONS_ANY,
      },
      { key: "offer_details", label: "Tell us a little more about what you can offer", type: "textarea", required: true },
      {
        key: "availability",
        label: "Is this available:",
        type: "choice",
        required: true,
        options: ["Once", "Occasionally", "Regularly", "Not sure yet"],
      },
      { key: "conditions", label: "Are there any conditions, limitations or timelines we should know about?", type: "textarea", required: false },
    ],
  },

  organisation: {
    note: "Choose this if you're reaching out on behalf of a business, NGO, school, clinic, community group, institution or other organisation and would like to explore how we could work together.",
    orgRequired: true,
    fields: [
      {
        key: "org_type",
        label: "Organisation type",
        type: "choice",
        required: true,
        other: true,
        options: ["NGO / foundation", "Business / brand", "School / college", "Community group", "Veterinary clinic / animal-care organisation", "Mental health / wellbeing organisation", "Sustainability / environmental organisation", "Government / civic organisation"],
      },
      { key: "org_website", label: "Website / social profile", type: "text", required: false, placeholder: "https://…" },
      {
        key: "topics",
        label: "What are you reaching out about?",
        type: "chips",
        required: true,
        other: true,
        options: ["Partnership", "CSR / funding", "Joint activity", "Volunteer opportunity", "Professional services", "Referring a need", "Offering resources", "Community initiative"],
      },
      {
        key: "pillars",
        label: "Which PilCoo pillar does this relate to?",
        type: "chips",
        required: true,
        pillarField: true,
        options: PILLAR_OPTIONS_MULTI,
      },
      { key: "idea", label: "Tell us what you have in mind", type: "textarea", required: true },
      {
        key: "org_contributions",
        label: "What could your organisation potentially contribute?",
        type: "chips",
        required: false,
        other: true,
        options: ["Expertise", "Volunteers", "Venue / space", "Products / supplies", "Financial support", "Community access", "Technology", "Media / awareness", "Logistics"],
      },
      {
        key: "expectations",
        label: "What would you be looking for from PilCoo?",
        type: "chips",
        required: false,
        other: true,
        options: ["Community participation", "Volunteers", "Awareness / amplification", "Co-creation / planning", "Subject-matter connections", "Introductions", "PilCoo participation", "Not sure yet"],
      },
      {
        key: "has_timeline",
        label: "Is there a specific timeline?",
        type: "choice",
        required: true,
        options: ["Yes", "No / flexible"],
      },
      { key: "proposed_date", label: "Proposed date", type: "text", required: true, showIf: { key: "has_timeline", oneOf: ["Yes"] }, placeholder: "e.g. mid-September 2026" },
      { key: "response_needed_by", label: "When would you ideally need a response?", type: "text", required: false, showIf: { key: "has_timeline", oneOf: ["Yes"] } },
    ],
  },

  cause: {
    note: "Choose this if you know of an animal, person, community, organisation or environmental need that you believe PilCoo should be aware of. Every submission will be reviewed before any action is taken.",
    extraNotice: "need",
    fields: [
      {
        key: "pillars",
        label: "Which pillar does this relate to?",
        type: "choice",
        required: true,
        pillarField: true,
        options: ["Animals", "People / Mental Wellbeing", "Nature", "More than one"],
      },
      {
        key: "who_needs",
        label: "Who or what needs support?",
        type: "choice",
        required: true,
        other: true,
        options: ["An animal", "Multiple animals", "An individual", "A family / community", "An organisation", "A public space / environmental cause"],
      },
      { key: "situation", label: "Tell us what is happening", type: "textarea", required: true },
      { key: "need_location", label: "Where is this need located?", type: "text", required: true, placeholder: "Area, city" },
      {
        key: "urgency",
        label: "How urgent is it?",
        type: "choice",
        required: true,
        urgencyField: true,
        options: ["Immediate / today", "Within the next few days", "Soon, but not urgent", "No immediate urgency", "Not sure"],
      },
      {
        key: "support_needed",
        label: "What kind of support may be needed?",
        type: "chips",
        required: false,
        other: true,
        options: ["Veterinary / animal-care coordination", "Food / supplies", "Financial support", "Volunteers", "Transport", "Professional expertise", "Awareness / amplification", "Community support", "Environmental action"],
      },
      { key: "approx_resources", label: "Approximate amount or resources needed", type: "text", required: false },
      {
        key: "anyone_helping",
        label: "Is anyone already helping or coordinating this?",
        type: "choice",
        required: true,
        options: ["Yes", "No", "Not sure"],
      },
      { key: "helper_name", label: "Person / organisation name", type: "text", required: false, showIf: { key: "anyone_helping", oneOf: ["Yes"] } },
      { key: "helper_contact", label: "Contact information", type: "text", required: false, showIf: { key: "anyone_helping", oneOf: ["Yes"] } },
      { key: "helper_website", label: "Website / social profile", type: "text", required: false, showIf: { key: "anyone_helping", oneOf: ["Yes"] } },
      { key: "supporting_link", label: "Supporting link", type: "text", required: false, placeholder: "https://…" },
    ],
  },

  collaborate: {
    note: "Choose this if you have an idea for a campaign, activity, event, initiative, content collaboration or other project that you'd like to explore with PilCoo.",
    fields: [
      {
        key: "reaching_out_as",
        label: "I'm reaching out as",
        type: "choice",
        required: true,
        other: true,
        options: ["Individual", "Business / brand", "NGO / foundation", "School / college", "Community group", "Veterinary clinic / animal-care organisation", "Mental health / wellbeing professional or organisation", "Sustainability / environmental organisation"],
      },
      { key: "org_website", label: "Website / social profile", type: "text", required: false, placeholder: "https://…", showIf: { key: "reaching_out_as", notOneOf: ["Individual"] } },
      {
        key: "collab_types",
        label: "What kind of collaboration are you thinking about?",
        type: "chips",
        required: true,
        other: true,
        options: ["Campaign / awareness initiative", "Event / community activity", "Content / storytelling", "Volunteer-led activity", "School / education initiative", "Animal welfare initiative", "Mental wellbeing initiative", "Sustainability / environmental initiative", "Corporate / CSR collaboration", "Product / in-kind support", "Technology / professional services"],
      },
      {
        key: "pillars",
        label: "Which PilCoo pillar does this connect to?",
        type: "choice",
        required: true,
        pillarField: true,
        options: PILLAR_OPTIONS_MULTI,
      },
      { key: "idea", label: "Tell us a little about the idea", type: "textarea", required: true },
      {
        key: "bring",
        label: "What could you or your organisation bring to the collaboration?",
        type: "chips",
        required: false,
        other: true,
        options: ["Professional expertise", "Volunteers", "Venue / space", "Products / supplies", "Financial support", "Content / media reach", "Technology", "Logistics / transport", "Community access", "Educational resources"],
      },
      {
        key: "need_from_pilcoo",
        label: "What would you need from PilCoo?",
        type: "chips",
        required: false,
        other: true,
        hint: "PilCoo is not a grant-making organisation — selecting financial support simply helps us understand expectations; it does not imply PilCoo provides grants or guarantees funding.",
        options: ["Community participation", "Volunteers", "Awareness / amplification", "Co-creation / planning", "Subject-matter connections", "Introductions / connections", "PilCoo participation", "Branding / co-branding", "Financial support", "Not sure yet"],
      },
      {
        key: "has_timeline",
        label: "Is there a specific timeline?",
        type: "choice",
        required: true,
        options: ["Yes", "Flexible / exploratory"],
      },
      { key: "proposed_date", label: "Proposed date", type: "text", required: true, showIf: { key: "has_timeline", oneOf: ["Yes"] }, placeholder: "e.g. mid-September 2026" },
      { key: "response_needed_by", label: "Response needed by", type: "text", required: false, showIf: { key: "has_timeline", oneOf: ["Yes"] } },
      {
        key: "location_scope",
        label: "Where would this happen?",
        type: "choice",
        required: true,
        other: true,
        options: ["Bengaluru", "Elsewhere in Karnataka", "Elsewhere in India", "Online", "Multiple locations"],
      },
      { key: "why_pilcoo", label: "Why PilCoo?", type: "textarea", required: false },
    ],
    orgRequiredWhen: { key: "reaching_out_as", notOneOf: ["Individual"] },
  },

  general: {
    note: "For anything that doesn't fit the options above, or if you simply want to ask us a question.",
    fields: [
      { key: "subject", label: "Subject", type: "text", required: true },
      { key: "message", label: "Message", type: "textarea", required: true },
    ],
  },
};

// Does a field's showIf condition pass given current answers?
export function fieldVisible(field, answers) {
  if (!field.showIf) return true;
  const val = answers[field.showIf.key];
  if (field.showIf.oneOf) {
    return Array.isArray(val) ? val.some((v) => field.showIf.oneOf.includes(v)) : field.showIf.oneOf.includes(val);
  }
  if (field.showIf.notOneOf) {
    if (val === undefined || val === "" || (Array.isArray(val) && val.length === 0)) return false;
    return Array.isArray(val) ? !val.some((v) => field.showIf.notOneOf.includes(v)) : !field.showIf.notOneOf.includes(val);
  }
  return true;
}

export function isOrgRequired(branchKey, answers) {
  const branch = BRANCHES[branchKey];
  if (!branch) return false;
  if (branch.orgRequired) return true;
  if (branch.orgRequiredWhen) {
    return fieldVisible({ showIf: branch.orgRequiredWhen }, answers);
  }
  return false;
}
